import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, CheckCircle2, CircleAlert, Cpu, Pause, Play, RefreshCw, ShieldCheck, XCircle } from "lucide-react";
import MissionDeliverables from "@/components/MissionDeliverables";
import MissionOutcome from "@/components/MissionOutcome";
import { apiFetch } from "@/lib/api";
import { Card, PageTitle, Spinner, useToast } from "@/components/earnos/kit";

const terminal = new Set(["completed", "completed_with_issues", "partially_completed", "failed", "cancelled"]);
const badge = "rounded-full border border-line bg-surface-3 px-2.5 py-1 text-xs font-semibold text-ink-2";
const action = "rounded-xl border border-line bg-surface-3 px-4 py-2 text-sm font-bold text-ink hover:border-brand";

export default function MissionControl() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search || ""), [location.search]);
  const toast = useToast();
  const [goal, setGoal] = useState("");
  useEffect(() => {
    if (typeof location.state?.missionGoal === "string") setGoal(location.state.missionGoal.slice(0, 2000));
  }, [location.key, location.state?.missionGoal]);
  const [reviewRevise, setReviewRevise] = useState(false);
  const [missions, setMissions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [overview, setOverview] = useState(null);
  const [usage, setUsage] = useState(null);
  const [capabilities, setCapabilities] = useState([]);
  const [selected, setSelected] = useState(() => searchParams.get("id"));
  useEffect(() => {
    const incoming = searchParams.get("id");
    if (incoming) setSelected(incoming);
  }, [searchParams]);
  const [mission, setMission] = useState(null);
  const [detailError, setDetailError] = useState("");
  const detailRequest = useRef(0);
  const activeSelection = useRef(selected);
  activeSelection.current = selected;
  const [answer, setAnswer] = useState("");
  const submitLock = useRef(false);
  const [busy, setBusy] = useState(false);
  const actionLock = useRef(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const [items, stats, tools] = await Promise.all([
        apiFetch("/mission/missions"), apiFetch("/mission/overview"), apiFetch("/mission/tools"),
      ]);
      setMissions(Array.isArray(items) ? items : []);
      setOverview(stats);
      setCapabilities(Array.isArray(tools) ? tools : []);
      apiFetch("/ai/usage").then(setUsage).catch(() => setUsage(null));
      setError("");
    } catch (e) {
      setError(e.message || "Mission Control is unavailable.");
    } finally {
      setLoading(false);
    }
  }, []);
  const refreshSelected = useCallback(async () => {
    if (!selected || activeSelection.current !== selected) return;
    const request = ++detailRequest.current;
    try {
      const result = await apiFetch("/mission/missions/" + encodeURIComponent(selected));
      if (request !== detailRequest.current || activeSelection.current !== selected) return;
      if (result?.mission_id !== selected) throw new Error("The mission response did not match. Please retry.");
      setMission(result);
      setDetailError("");
    } catch (e) {
      if (request !== detailRequest.current || activeSelection.current !== selected) return;
      setMission(null);
      setDetailError(e.message || "Cannot load this mission.");
    }
  }, [selected]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    setMission(null);
    setDetailError("");
    setAnswer("");
    refreshSelected();
    return () => { detailRequest.current += 1; };
  }, [refreshSelected]);
  useEffect(() => {
    const incoming = searchParams.get("goal");
    if (incoming && incoming.length >= 8) setGoal(incoming.slice(0, 2000));
  }, [searchParams]);
  useEffect(() => {
    const interval = setInterval(() => { refresh(); refreshSelected(); }, 10000);
    return () => clearInterval(interval);
  }, [refresh, refreshSelected]);

  async function submitGoal(e) {
    e.preventDefault();
    if (goal.trim().length < 8 || submitLock.current || usage?.hard_cap_reached) return;
    submitLock.current = true;
    setBusy(true);
    try {
      const result = await apiFetch("/mission/missions", { method: "POST", body: { goal: goal.trim(), source: "web", options: { review_revise: reviewRevise } } });
      setGoal("");
      setSelected(result.mission_id);
      navigate("/mission?id=" + encodeURIComponent(result.mission_id), { replace: true });
      toast.show("Mission created. EARNOS will show work and request approval when needed.", "success");
      await refresh();
    } catch (e) { toast.show(e.message, "error"); }
    finally { submitLock.current = false; setBusy(false); }
  }

  async function change(actionName, path, body) {
    if (submitLock.current) return;
    submitLock.current = true;
    setBusy(true);
    try {
      await apiFetch(path, { method: "POST", ...(body ? { body } : {}) });
      toast.show(actionName + " saved", "success");
      await refreshSelected(); await refresh();
    } catch (e) { toast.show(e.message, "error"); }
    finally { submitLock.current = false; setBusy(false); }
  }

  return (
    <div className="animate-fade-in space-y-5" data-testid="earnos-mission-control">
      <section className="rounded-3xl border border-brand/30 bg-gradient-to-br from-surface-2 via-surface-2 to-brand/10 p-5 sm:p-8 space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-2 max-w-2xl">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-brand-light">EARNOS · Your AI work operator</p>
            <PageTitle>Tell EARNOS the outcome. Not just the task.</PageTitle>
            <p className="text-sm sm:text-base text-ink-2">EARNOS plans, uses connected tools with your permission, executes what is available, and checks results. You stay in control of accounts, publishing and payments.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button type="button" onClick={() => navigate(selected ? "/chat?mission=" + encodeURIComponent(selected) : "/chat")} className={action}>Talk to EARNOS</button>
            <button type="button" onClick={() => navigate("/connections")} className={action}>Connect your apps <ArrowRight size={16} className="inline ml-1" /></button>
          </div>
        </div>
        {usage?.hard_cap_reached ? <div role="status" className="rounded-xl border border-amber-400/40 p-4 space-y-2">
          <p className="font-bold text-ink">Monthly AI capacity reached</p>
          <p className="text-sm text-ink-2">Your {usage.plan} plan has used {usage.month?.requests} of {usage.limits?.requests} AI calls and {Number(usage.month?.est_cost_usd || 0).toFixed(2)} USD of its {Number(usage.limits?.cost || 0).toFixed(2)} USD estimated AI budget. Either limit pauses new AI work. Connections remain available.</p>
          <button type="button" className={action} onClick={() => navigate("/billing")}>Review plan and capacity</button>
        </div> : null}
        <form onSubmit={submitGoal} className="space-y-3">
          <label htmlFor="mission-goal" className="block font-bold text-ink">What result do you want EARNOS to achieve?</label>
          <textarea id="mission-goal" data-testid="mission-goal" rows={4} maxLength={2000} value={goal}
            onChange={e => setGoal(e.target.value)}
            className="w-full rounded-xl border border-line bg-surface-3 p-3 text-ink outline-none focus:border-brand"
            placeholder="Describe your goal, what to deliver, and which accounts EARNOS may use." />
          <label className="flex items-start gap-3 text-sm text-ink" htmlFor="mission-review-revise">
            <input id="mission-review-revise" data-testid="mission-review-revise" type="checkbox"
              checked={reviewRevise} onChange={e => setReviewRevise(e.target.checked)} disabled={busy}
              aria-describedby="mission-review-cost" className="mt-1 accent-brand" />
            <span>Review and revise the result
              <span id="mission-review-cost" className="block text-xs text-ink-2">Optional extra AI review. Uses additional AI credits. Off by default.</span>
            </span>
          </label>
          <button type="submit" disabled={busy || goal.trim().length < 8 || usage?.hard_cap_reached}
            className="rounded-xl bg-brand px-5 py-3 text-sm font-bold text-white disabled:opacity-50">Start mission</button>
        </form>
      </section>
      <Card><details><summary className="font-bold cursor-pointer">Supported capabilities and current availability</summary>
        <p className="text-xs text-ink-2 mt-2">These statuses come from the same server registry used by the mission planner. AI and service costs depend on the task and provider.</p>
        <div className="space-y-3 mt-3">{capabilities.map(tool=><div key={tool.name} className="border border-line rounded-xl p-3">
          <p className="font-semibold text-sm">{tool.name.replaceAll("_", " ")} · {tool.status.replaceAll("_", " ")}</p>
          <p className="text-xs text-ink-2 mt-1">{tool.description}</p>
          <p className="text-xs text-ink-2 mt-1">Verification: {tool.verification_method}. Runtime limit: {tool.max_runtime_seconds}s.</p>
        </div>)}</div>
      </details></Card>
      {error ? <p role="alert" className="text-sm text-danger"><CircleAlert size={16} className="inline mr-1" />{error}</p> : null}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[["Missions", overview?.missions], ["Running", overview?.running],
          ["Need you", overview?.needs_you], ["Verified steps", overview?.verified_tasks]].map(([name, number]) => (
          <Card key={name} className="!p-4"><p className="text-xs text-ink-2">{name}</p><p className="text-2xl font-extrabold text-ink">{number ?? "—"}</p></Card>
        ))}
      </div>
      <p className="text-xs text-ink-2">Verified steps mean tools passed their checks—not that business outcomes, sales, or income are confirmed.</p>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)]">
        <Card>
          <div className="flex items-center justify-between mb-3">
            <div><h2 className="font-bold text-ink">Your missions</h2><p className="text-xs text-ink-2 mt-1">{showHistory ? "All saved missions" : "Active missions and recent results"}</p></div>
            <button aria-label="Refresh missions" className={action} onClick={() => { refresh(); refreshSelected(); }}><RefreshCw size={16} /></button>
          </div>
          {loading ? <Spinner /> : missions.length === 0 ? <p className="text-sm text-ink-2">No missions yet. Start one above.</p> : (
            <div className="space-y-2">
              {(showHistory ? missions : [...missions.filter(m => !terminal.has(m.state)), ...missions.filter(m => terminal.has(m.state)).slice(0, 2)]).map(m => (
                <button key={m.mission_id} onClick={() => { setSelected(m.mission_id); navigate("/mission?id=" + encodeURIComponent(m.mission_id), { replace: true }); }}
                  className={"w-full rounded-xl border p-3 text-left " + (selected === m.mission_id ? "border-brand bg-brand/10" : "border-line bg-surface-3")}>
                  <p className="text-sm font-semibold text-ink line-clamp-2">{m.goal}</p>
                  <p className="text-xs text-ink-2 mt-2">{m.state?.replaceAll("_", " ")} · {m.stage}</p>
                </button>
              ))}
              {missions.length > 2 ? <button type="button" className="w-full rounded-xl border border-line px-3 py-2 text-xs font-semibold text-brand-light hover:border-brand"
                onClick={() => setShowHistory(x => !x)}>
                {showHistory ? "Show current work" : "View full mission history"}
              </button> : null}
            </div>
          )}
        </Card>
        <Card>
          {!selected ? <div className="py-10 text-center text-ink-2"><Cpu className="mx-auto mb-2" />Select a mission to see its actual progress.</div> :
          detailError ? <div role="alert" className="space-y-3">
            <p className="text-sm text-danger">{detailError}</p>
            <button className={action} onClick={refreshSelected}>Retry mission details</button>
          </div> : !mission || mission.mission_id !== selected ? <div role="status" aria-label="Loading mission details"><Spinner /></div> : (
            <div className="space-y-4">
              <div className="flex flex-wrap justify-between items-center gap-2">
                <h2 className="font-bold text-ink">Mission details</h2>
                <span className={badge}>{mission.state?.replaceAll("_", " ")}</span>
              </div>
              <p className="text-sm text-ink-2">{mission.goal}</p>
              <MissionOutcome mission={mission} />
              {mission.attention ? <p role="status" className="p-3 rounded-xl border border-line bg-surface-3 text-ink">{typeof mission.attention === "string" ? mission.attention : typeof mission.attention.message === "string" ? mission.attention.message : "This mission needs your review."}</p> : null}
              <div className="flex flex-wrap gap-2">
                {(mission.stages || []).map(s => <span key={s.stage} className={badge}>{s.status === "done" ? "✓ " : ""}{s.stage}</span>)}
              </div>
              {mission.state === "needs_connection" ? <button onClick={() => navigate("/connections")} className={action}>Connect required app</button> : null}
              {mission.state === "needs_input" ? (
                <div className="space-y-2">
                  {(mission.questions || []).map((q,i) => <p key={i} className="text-sm text-ink">{q}</p>)}
                  <textarea value={answer} onChange={e => setAnswer(e.target.value)} className="w-full rounded-xl p-3 bg-surface-3 border border-line text-ink" rows={3} aria-label="Clarification" />
                  <button disabled={busy || !answer.trim()} className={action} onClick={() => change("Answer", "/mission/missions/" + encodeURIComponent(selected) + "/answers", { answers: { clarification: answer.trim() } })}>Submit answer</button>
                </div>
              ) : null}
              {(mission.approvals || []).filter(a => a.status === "pending").map(a => (
                <div key={a.approval_id} className="rounded-xl border border-brand/50 bg-surface-3 p-4">
                  <p className="flex items-center gap-2 text-sm font-bold text-ink"><ShieldCheck size={18} /> Your approval required</p>
                  <p className="text-sm text-ink mt-2">{a.action}</p>
                  <p className="text-xs text-ink-2 mt-1">Tool: {a.tool}. Expires: {a.expires_at ? new Date(a.expires_at).toLocaleString() : "New preview required"}. Review the exact proposed action and any externally visible change.</p>
                  <details className="mt-2">
                    <summary className="text-sm text-brand-light cursor-pointer">Review exact action</summary>
                    <pre className="mt-2 overflow-auto max-h-64 whitespace-pre-wrap break-all text-xs text-ink-2">{JSON.stringify({ args: a.args, preview: a.preview }, null, 2)}</pre>
                  </details>
                  {a.tool === "publish_artifact" && <p className="text-xs text-ink-2 mt-2">Sharing applies only to the reviewed document version. Refreshing the preview does not publish it.</p>}
                  <div className="flex flex-wrap gap-2 mt-3">
                    {a.tool === "publish_artifact" && <button disabled={busy} className={action} onClick={() => change("Sharing preview", "/mission/approvals/" + encodeURIComponent(a.approval_id) + "/refresh")}>Refresh sharing preview</button>}
                    <button disabled={busy} className="rounded-xl bg-brand px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
                      onClick={() => change("Approval", "/mission/approvals/" + encodeURIComponent(a.approval_id) + "/approve", a.tool === "publish_artifact" ? { pdf_sha256: a.preview?.pdf_sha256 } : undefined)}>Approve action</button>
                    <button disabled={busy} className={action} onClick={() => change("Decline", "/mission/approvals/" + encodeURIComponent(a.approval_id) + "/decline")}>Decline</button>
                  </div>
                </div>
              ))}
              <div className="space-y-2">
                <h3 className="font-semibold text-ink">Real task results</h3>
                {(mission.tasks || []).length === 0 ? <p className="text-sm text-ink-2">{terminal.has(mission.state) || ["blocked", "paused"].includes(mission.state) ? "No task results were produced before this mission stopped." : "EARNOS is preparing the plan."}</p> : (mission.tasks || []).map(t =>
                  <div key={t.id} className="border border-line rounded-xl p-3">
                    <div className="flex items-center gap-2">
                      {t.status === "verified" ? <CheckCircle2 size={16} className="text-ok" /> : t.status?.startsWith("failed") ? <XCircle size={16} className="text-danger" /> : <Cpu size={16} className="text-ink-2" />}
                      <p className="text-sm font-semibold text-ink">{t.title || t.tool}</p>
                    </div>
                    <p className="text-xs text-ink-2 mt-1">{t.status}{t.duration_seconds != null ? " · " + t.duration_seconds + "s" : ""}</p>
                    {t.verification ? <p className="text-xs text-ink-2 mt-1 break-words">{String(t.verification)}</p> : null}
                    {t.error ? <p className="text-xs text-danger mt-1 break-words">{t.error}</p> : null}
                  </div>
                )}
              </div>
              {(mission.logs || []).length > 0 ? <details open className="rounded-xl border border-line p-3">
                <summary className="font-semibold text-ink">EARNOS activity</summary>
                <ol className="mt-3 space-y-2">{mission.logs.slice(-20).map(entry => <li key={entry.log_id} className="text-sm text-ink-2 break-words"><time className="text-xs mr-2">{new Date(entry.at).toLocaleTimeString()}</time>{entry.message}</li>)}</ol>
              </details> : null}
              {(mission.artifacts || []).length > 0 ? <MissionDeliverables key={mission.mission_id} mission={mission} /> : null}
              {["completed", "completed_with_issues", "partially_completed"].includes(mission.state) && (mission.tasks || []).some(t => ["write_document", "write_spreadsheet"].includes(t.tool) && t.result?.artifact_id) ? <div className="space-y-2">
                <button disabled={busy} className={action} onClick={() => change("Recheck saved files", "/mission/missions/" + encodeURIComponent(selected) + "/recheck-documents")}>Recheck saved files</button>
                <p className="text-xs text-ink-2">Reads the saved files again. Does not repeat external actions. Failed checks correct the completion record.</p>
              </div> : null}
              {!terminal.has(mission.state) ? <div className="flex flex-wrap gap-2">
                {mission.state === "paused" || mission.state === "needs_connection" || mission.blocker?.code === "ai_capacity_reached" ?
                  <button disabled={busy || (mission.blocker?.code === "ai_capacity_reached" && usage?.hard_cap_reached)} className={action} onClick={() => change("Resume", "/mission/missions/" + encodeURIComponent(selected) + "/resume")}><Play size={15} className="inline mr-1" /> Resume</button> :
                  <button disabled={busy} className={action} onClick={() => change("Pause", "/mission/missions/" + encodeURIComponent(selected) + "/pause")}><Pause size={15} className="inline mr-1" /> Pause</button>}
                <button disabled={busy} className={action} onClick={() => {
                  if (window.confirm("Cancel this mission? Work already completed outside EARNOS may not be reversible.")) change("Cancel", "/mission/missions/" + encodeURIComponent(selected) + "/cancel");
                }}><XCircle size={15} className="inline mr-1" /> Cancel</button>
              </div> : null}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
